"use client";

import React from "react";
import { Phone, ArrowRight, ShieldCheck, Building2, House as Home, Layers, Wrench, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const services = [{
  icon: Building2,
  title: "Commercial",
  description: "Multi-storey site access, public protection, and phased builds."
}, {
  icon: Home,
  title: "Residential",
  description: "Extensions, chimney stacks, re-pointing, and exterior maintenance."
}, {
  icon: Layers,
  title: "Roofing & solar",
  description: "Temporary roofs, edge protection, and bespoke solar array access."
}, {
  icon: Wrench,
  title: "Specialist access",
  description: "Cantilevered rigs, tight-access plots, and internal birdcages."
}];
const accreditations = ["NASC Member", "CHAS Accredited"];
export default function WycombeHero() {
  return <section id="highwycombehero" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-20 lg:py-28">
      {/* Decorative dark background scaffolding grid pattern */}
      <div className="pointer-events-none absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-primary via-transparent to-transparent" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 opacity-5 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]" aria-hidden="true" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Column: Core Value Proposition & CTAs */}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary-foreground text-xs font-semibold px-3 py-1">
                High Wycombe & South Buckinghamshire
              </Badge>
              <div className="hidden sm:flex items-center gap-2 text-xs text-secondary-foreground/70">
                <span className="inline-block size-1.5 rounded-full bg-primary" />
                <span>Safety-led family scaffolding contractor</span>
              </div>
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl font-default">
                Scaffolding in High Wycombe
              </h1>
              <p className="max-w-2xl text-lg sm:text-xl text-secondary-foreground/80 leading-relaxed">
                Commercial, residential, roofing, and specialist access scaffolding across High Wycombe and South Buckinghamshire — planned safely, erected promptly, and fully certified.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold text-base h-12 px-6">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border/40 bg-secondary/80 text-white hover:bg-secondary hover:text-white focus:text-white h-12 px-6 text-base font-semibold">
                <a href="tel:01344929900" className="flex items-center justify-center gap-2 text-white hover:text-white">
                  <Phone className="size-4 text-primary shrink-0" />
                  <span className="text-white">Call 01344 929900</span>
                </a>
              </Button>
            </div>

            {/* Accreditations bar */}
            <div className="pt-6 border-t border-secondary-border/50">
              <p className="text-xs uppercase tracking-wider text-secondary-foreground/60 mb-3 font-semibold">
                Confirmed Safety Accreditations
              </p>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                {accreditations.map((item, i) => <div key={item} data-index={i} className="flex items-center gap-2 text-sm text-secondary-foreground/90">
                    <ShieldCheck className="size-4 text-primary shrink-0" />
                    <span className="font-medium">{item}</span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Right Column: Capabilities Card Matrix */}
          <div className="lg:col-span-5">
            <div className="rounded-xl border border-secondary-border/60 bg-sidebar/70 backdrop-blur-md p-6 sm:p-8 shadow-xl">
              <div className="flex items-center justify-between pb-6 border-b border-secondary-border/50">
                <div>
                  <h2 className="text-xl font-bold text-white font-default">
                    Scope of Services
                  </h2>
                  <p className="text-xs text-secondary-foreground/70 mt-0.5">
                    TG20:21 compliant scaffolding across High Wycombe
                  </p>
                </div>
                <div className="flex items-center gap-1 text-xs text-primary font-semibold">
                  <CheckCircle2 className="size-4" />
                  <span>20+ Years Exp.</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6">
                {services.map((service, i) => {
                const Icon = service.icon;
                return <div key={service.title} data-index={i} className="group rounded-lg border border-secondary-border/40 bg-secondary/50 p-4 transition-colors hover:border-primary/50 hover:bg-secondary/80">
                      <div className="mb-3 flex size-9 items-center justify-center rounded-md bg-primary/15 text-primary">
                        <Icon className="size-5" />
                      </div>
                      <h3 className="text-sm font-semibold text-white">
                        {service.title}
                      </h3>
                      <p className="mt-1 text-xs text-secondary-foreground/70 leading-relaxed">
                        {service.description}
                      </p>
                    </div>;
              })}
              </div>

              <div className="mt-6 rounded-lg bg-secondary/90 border border-secondary-border/40 p-4 text-xs text-secondary-foreground/80 flex items-center justify-between gap-3">
                <span className="leading-snug">
                  Need an urgent site survey or scaffolding design in High Wycombe?
                </span>
                <Button asChild variant="link" size="sm" className="text-primary hover:text-primary/80 font-semibold p-0 h-auto shrink-0">
                  <Link to="/ContactRequestQuotePage">
                    Book survey
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}